/**
 * 신 JIS를 이용한 계산 방법임 (Troughed Conveyor용)
 */

import styled from 'styled-components';
// import palette from '../../../lib/styles/palette';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { changeBody } from '../../../modules/conveyor/conveyorWriteSlice';

import { Input, Output, Select } from '../common/SingleInput';
import { MultiOutput } from '../common/MultiInput';

import {
    beltCarcassType,
    beltWidthRating,
    getLengthCoefficient,
    getWidthRelatedValue,
    getTentativeTension,
    // selectMotor,
} from './library-jis';
import { getIdlerInfo } from '../../../lib/data/idler-data';

import CopyFromOhtersButton from './CopyFromOhtersButton';
import AlertLabel from '../../common/AlertLabel';
import { LoadNoLoadLabel } from '../common/LoadNoLoadLabel';

const PowerCalculationJIS = () => {
    // 언어설정 가져오기
    const language = useSelector((state) => state.language.language);
    // const isKR = language === 'KR' ? true : false;
    const isJP = language === 'JP' ? true : false;
    const isEN = language === 'EN' ? true : false;

    const dispatch = useDispatch();

    // 스토어 가져오기
    const prevInput = useSelector((state) => state.conveyorWrite.body.input);
    const prevOutput = useSelector((state) => state.conveyorWrite.body.output);
    const prevResistance = useSelector((state) => state.conveyorWrite.body.resistance);
    // const prevPower = useSelector((state) => state.conveyorWrite.body.power);

    // useState로 상태 관리
    const [input, setInput] = useState(
        prevInput.capacity
            ? prevInput
            : {
                  capacity: 1000,
                  length: 100,
                  height: 20,
                  materialConveyed: 'Coal',
                  specificGravity: 830,
                  beltSpeed: 4.17,
                  beltWidth: 1400,
                  carcassType: 'EP',
                  artificialFrictionCoeff: 0.03,
                  qtyPulley: 6,
                  carrySpacing: 1.0,
                  returnSpacing: 3.0,
                  troughAngle: 35,
                  initialSpeed: 0.0,
                  tiltAngle: 2.0,
                  tiltLength: 80,
                  frictionCoeffIdlerBelt: 0.4,
                  frictionCoeffMaterialBelt: 0.7,
                  frictionCoeffBeltSkirt: 0.7,
                  frictionCoeffBeltCleaner: 0.5,
                  qtyBeltCleaner: 2,
                  cleanerContactPressure: 65000,
                  qtyUnloadingScraper: 0,
                  scrapingCoeff: 1500,
                  skirtLength: 5.0,
              },
    );
    const [output, setOutput] = useState(prevOutput || {});
    const [resistance, setResistance] = useState(prevResistance || {});
    // const [power, setPower] = useState(prevPower || {});
    // const [tension, setTension] = useState({});
    // const [belt, setBelt] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /* LengthCoeff 계산 */
    useEffect(() => {
        if (!input.length) return;

        const _lengthCoeff = getLengthCoefficient(input.length);

        setOutput((prev) => ({
            ...prev,
            lengthCoeff: Math.round(_lengthCoeff * 100) / 100,
        }));
    }, [input.length]);

    /* Slope Angle 계산 */
    useEffect(() => {
        if (!input.length || !input.height) return;

        const _slopeAngle = (Math.atan(input.height / input.length) * 180) / Math.PI;
        setOutput((prev) => ({
            ...prev,
            slopeAngle: Math.round(_slopeAngle * 100) / 100,
        }));
    }, [input.height, input.length]);

    /* Trough Angle Coefficient 계산 */
    useEffect(() => {
        if (!input.troughAngle) return;
        const x0 = 30;
        const y0 = 0.4;
        const x1 = 45;
        const y1 = 0.5;

        const interpolate = (x) => {
            const y = y0 + ((y1 - y0) / (x1 - x0)) * (x - x0);
            return y;
        };

        let _troughAngleCoeff;

        if (input.troughAngle < 30) {
            _troughAngleCoeff = 0.4;
        } else if (input.troughAngle < 40) {
            _troughAngleCoeff = interpolate(input.troughAngle);
        } else {
            _troughAngleCoeff = 0.5;
        }

        setOutput((prev) => ({
            ...prev,
            troughAngleCoeff: Math.round(_troughAngleCoeff * 100) / 100,
        }));
    }, [input.troughAngle]);

    /* Carcass Type에 따른 벨트 Safety Factor 산출 */
    // useEffect(() => {
    //     if (!input.carcassType) return;

    //     let _SF;
    //     if (input.carcassType === 'ST') {
    //         _SF = 7;
    //     } else {
    //         _SF = 12;
    //     }
    //     setBelt((prev) => ({
    //         ...prev,
    //         safetyFactor: _SF,
    //     }));
    // }, [input.carcassType]);

    /* Belt, Idler 단위 중량 산정 */
    useEffect(() => {
        if (!input.carrySpacing || !input.returnSpacing || !input.beltWidth) return;

        const bw = parseFloat(input.beltWidth, 10);
        const _beltThick = getWidthRelatedValue(input.beltWidth, 'beltThick');
        const _massBelt = getWidthRelatedValue(input.beltWidth, 'massBelt');
        const _rotatingMassCarry = getWidthRelatedValue(input.beltWidth, 'rotatingMassCarry');
        const _rotatingMassReturn = getWidthRelatedValue(input.beltWidth, 'rotatingMassReturn');
        const _massCarryIdler = Math.round((_rotatingMassCarry / input.carrySpacing) * 10) / 10;
        const _massReturnIdler = Math.round((_rotatingMassReturn / input.returnSpacing) * 10) / 10;
        const skirtWidth = Math.round((((input.beltWidth / 1000) * 2) / 3) * 100) / 100;
        // const _cleanerContactArea =
        //     (bw * 16 * 2 + (bw * 16) / Math.cos((60 / 180) * Math.PI)) /
        //     10 ** 6;
        const qtyBeltCleaner = input.qtyBeltCleaner || 2;
        const _cleanerContactArea = (bw * 0.001 * 2 * qtyBeltCleaner) / 1000;

        setOutput((prev) => ({
            ...prev,
            massCarryIdler: _massCarryIdler,
            massReturnIdler: _massReturnIdler,
            massBelt: _massBelt,
            beltThick: _beltThick,
            skirtWidth,
            cleanerContactArea: Math.round(_cleanerContactArea * 100000) / 100000,
        }));
    }, [input.beltWidth, input.carrySpacing, input.qtyBeltCleaner, input.returnSpacing]);

    /* Volumetric Capacity, Mass Material */
    useEffect(() => {
        if (!input.capacity || !input.beltSpeed) return;

        const volumetricCapacity =
            Math.round(((1000 * input.capacity) / (3600 * input.specificGravity)) * 10000) / 10000;
        const massMaterial = Math.round(((volumetricCapacity * input.specificGravity) / input.beltSpeed) * 10) / 10;
        setOutput((prev) => ({
            ...prev,
            volumetricCapacity,
            massMaterial,
        }));
    }, [input.beltSpeed, input.capacity, input.specificGravity]);

    /* Acceleration Length */
    useEffect(() => {
        if (!input.beltSpeed || !input.frictionCoeffMaterialBelt) return;

        const v = parseFloat(input.beltSpeed);
        const v0 = parseFloat(input.initialSpeed);
        const g = 9.806;
        const mu1 = parseFloat(input.frictionCoeffMaterialBelt);
        const accelerationLength = Math.round(((v ** 2 - v0 ** 2) / (2 * g * mu1)) * 100) / 100;
        // const _skirtLength = accelerationLength + 2.5;
        const _minSkirtLength = accelerationLength + 3.5; // 이건 일반적인 수치가 아님. 이 계산에서는 사용하지 않음.

        setOutput((prev) => ({
            ...prev,
            accelerationLength,
            minSkirtLength: Math.round(_minSkirtLength * 100) / 100,
        }));
    }, [input.beltSpeed, input.frictionCoeffMaterialBelt, input.initialSpeed]);

    /* Resistance 계산 */
    useEffect(() => {
        // 변수 변환
        const f = Number(input.artificialFrictionCoeff);
        const Q = Number(input.capacity);
        const L = Number(input.length);
        const H = Number(input.height);
        const C = Number(output.lengthCoeff);
        const g = 9.8; //
        const qRO = output.massCarryIdler;
        const qRU = output.massReturnIdler;
        const qB = output.massBelt;
        const qG = output.massMaterial;
        const delta = (output.slopeAngle * Math.PI) / 180; // Radian으로 미리 변환
        const rou = Number(input.specificGravity);
        const v = Number(input.beltSpeed);
        const v0 = Number(input.initialSpeed);
        const Iv = output.volumetricCapacity;
        const mu0 = Number(input.frictionCoeffIdlerBelt);
        const mu1 = Number(input.frictionCoeffMaterialBelt);
        const mu2 = Number(input.frictionCoeffBeltSkirt);
        const mu3 = Number(input.frictionCoeffBeltCleaner);
        const lb = output.accelerationLength;
        const b1 = output.skirtWidth;
        // const l = output.skirtLength;
        const l = Number(input.skirtLength);
        const B = Number(input.beltWidth) / 1000; // m 단위로 변경
        // const F = 21002; // 평균 벨트 텐션
        // const Fr = 42004; // 평균 Resultant Force
        const [F, Fr] = getTentativeTension(f, Q, L, v, g, qRO, qRU, qB, H);
        const carcassFactor1 = input.carcassType === 'ST' ? 12 : 9;
        const carcassFactor2 = input.carcassType === 'ST' ? 200 : 140;
        const D = 1000; // d0/D를 0.24로 하기 위해
        const d0 = 240; // d0/D를 0.24로 하기 위해
        const epsilon = (Number(input.tiltAngle) * Math.PI) / 180; // Radian으로 미리 변환
        const Cepsilon = output.troughAngleCoeff;
        const Lepsilon = Number(input.tiltLength);
        const A = output.cleanerContactArea;
        const P = Number(input.cleanerContactPressure);
        const ka = Number(input.scrapingCoeff);
        const N = parseInt(input.qtyPulley);

        // 텐션 계산
        // console.log('f, L, g, qRO, qRU, qB, qG ', f, L, g, qRO, qRU, qB, qG);
        const _mainResistanceLoad =
            // f * L * g * (qRO + qRU + (2 * qB + qG) * Math.cos(delta));
            f * L * g * (qRO + qRU + (2 * qB + qG) * 1);
        const _mainResistanceNoLoad = f * L * g * (qRO + qRU + 2 * qB * 1);
        const _secondary1Load = L > 80 ? 'incl.' : Math.ceil(Iv * rou * (v - v0));
        const _secondary1NoLoad = L > 80 ? 'incl.' : Math.ceil(0 * rou * (v - v0));
        const _secondary2Load =
            L > 80 ? 'incl.' : Math.ceil((mu2 * Iv ** 2 * rou * g * lb) / (((v + v0) / 2) ** 2 * b1 ** 2));
        const _secondary2NoLoad =
            L > 80 ? 'incl.' : Math.ceil((mu2 * 0 ** 2 * rou * g * lb) / (((v + v0) / 2) ** 2 * b1 ** 2));
        const _secondary3Load =
            L > 80 ? 'incl.' : Math.ceil(carcassFactor1 * B * (carcassFactor2 + (0.01 * F) / B) * 0.02 * N); // d/D = 0.02로 정함.
        const _secondary3NoLoad =
            L > 80 ? 'incl.' : Math.ceil(carcassFactor1 * B * (carcassFactor2 + (0.01 * F) / B) * 0.02 * N); // d/D = 0.02로 정함.
        const _secondary4Load = L > 80 ? 'incl.' : Math.ceil(0.005 * (d0 / D) * Fr * (N - 1));
        const _secondary4NoLoad = L > 80 ? 'incl.' : Math.ceil(0.005 * (d0 / D) * Fr * (N - 1));
        const _secondaryLoad =
            L > 80
                ? _mainResistanceLoad * (C - 1)
                : _secondary1Load + _secondary2Load + _secondary3Load + _secondary4Load;
        const _secondaryNoLoad =
            L > 80
                ? _mainResistanceNoLoad * (C - 1)
                : _secondary1NoLoad + _secondary2NoLoad + _secondary3NoLoad + _secondary4NoLoad;

        const _specialMain1Load =
            Cepsilon *
            mu0 *
            Lepsilon *
            (qB + qG) *
            g *
            (output.slopeAngle < 18 ? 1 : Math.cos(delta)) *
            Math.sin(epsilon);

        const _specialMain1NoLoad =
            Cepsilon *
            mu0 *
            Lepsilon *
            (qB + 0) *
            g *
            (output.slopeAngle < 18 ? 1 : Math.cos(delta)) *
            Math.sin(epsilon);
        const _specialMain2Load = (mu1 * Iv ** 2 * rou * g * l) / (v ** 2 * b1 ** 2);
        const _specialMain2NoLoad = (mu1 * 0 ** 2 * rou * g * l) / (v ** 2 * b1 ** 2);
        const _specialMain3Load = A * P * mu3;
        const _specialMain3NoLoad = A * P * mu3;
        const _specialMain4Load = B * ka;
        const _specialMain4NoLoad = B * ka;
        const _specialMainLoad = _specialMain1Load + _specialMain2Load + _specialMain3Load + _specialMain4Load;
        const _specialMainNoLoad =
            _specialMain1NoLoad + _specialMain2NoLoad + _specialMain3NoLoad + _specialMain4NoLoad;
        const _slopeLoad = qG * H * g;
        const _slopeNoLoad = 0 * H * g;

        const _overallLoad =
            _mainResistanceLoad + _secondaryLoad + _specialMainLoad + _slopeLoad; /*+ _horizontalCurveLoad*/
        const _overallNoLoad =
            _mainResistanceNoLoad + _secondaryNoLoad + _specialMainNoLoad + _slopeNoLoad; /*+ _horizontalCurveNoLoad*/

        const _reverseLoad = _slopeLoad - 0.5 * (_mainResistanceLoad + _secondaryLoad); /*+ _horizontalCurveLoad*/
        const _reverseNoLoad = 0;

        setResistance((prev) => ({
            ...prev,
            main: {
                load: Math.ceil(_mainResistanceLoad),
                noLoad: Math.ceil(_mainResistanceNoLoad),
            },
            secondary: {
                load: Math.ceil(_secondaryLoad),
                noLoad: Math.ceil(_secondaryNoLoad),
            },
            secondary1: {
                load: Math.ceil(_secondary1Load),
                noLoad: Math.ceil(_secondary1NoLoad),
            },
            secondary2: {
                load: Math.ceil(_secondary2Load),
                noLoad: Math.ceil(_secondary2NoLoad),
            },
            secondary3: {
                load: Math.ceil(_secondary3Load),
                noLoad: Math.ceil(_secondary3NoLoad),
            },
            secondary4: {
                load: Math.ceil(_secondary4Load),
                noLoad: Math.ceil(_secondary4NoLoad),
            },
            specialMain: {
                load: Math.ceil(_specialMainLoad),
                noLoad: Math.ceil(_specialMainNoLoad),
            },
            specialMain1: {
                load: Math.ceil(_specialMain1Load),
                noLoad: Math.ceil(_specialMain1NoLoad),
            },
            specialMain2: {
                load: Math.ceil(_specialMain2Load),
                noLoad: Math.ceil(_specialMain2NoLoad),
            },
            specialMain3: {
                load: Math.ceil(_specialMain3Load),
                noLoad: Math.ceil(_specialMain3NoLoad),
            },
            specialMain4: {
                load: Math.ceil(_specialMain4Load),
                noLoad: Math.ceil(_specialMain4NoLoad),
            },
            slope: {
                load: Math.ceil(_slopeLoad),
                noLoad: Math.ceil(_slopeNoLoad),
            },
            reverse: {
                load: Math.ceil(_reverseLoad),
                noLoad: Math.ceil(_reverseNoLoad),
            },
            overall: {
                load: Math.ceil(_overallLoad),
                noLoad: Math.ceil(_overallNoLoad),
            },
        }));
    }, [
        input.artificialFrictionCoeff,
        input.beltSpeed,
        input.beltWidth,
        input.capacity,
        input.carcassType,
        input.cleanerContactPressure,
        input.frictionCoeffBeltCleaner,
        input.frictionCoeffBeltSkirt,
        input.skirtLength,
        input.frictionCoeffIdlerBelt,
        input.frictionCoeffMaterialBelt,
        input.height,
        input.initialSpeed,
        input.length,
        input.qtyPulley,
        input.scrapingCoeff,
        input.specificGravity,
        input.tiltAngle,
        input.tiltLength,
        output.accelerationLength,
        output.cleanerContactArea,
        output.lengthCoeff,
        output.massBelt,
        output.massCarryIdler,
        output.massMaterial,
        output.massReturnIdler,
        // output.skirtLength,
        output.skirtWidth,
        output.slopeAngle,
        output.troughAngleCoeff,
        output.volumetricCapacity,
        // horizontalCurveResistance
    ]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        dispatch(
            changeBody({
                key: 'resistance',
                value: resistance,
            }),
        );
    }, [dispatch, resistance]);

    /* Power Calculation */
    // useEffect(() => {
    //     if (!input.beltSpeed || !resistance.overall) return;

    //     const FU = resistance.overall;
    //     const v = parseFloat(input.beltSpeed);
    //     const _actualPower = (FU * v) / 1000;

    //     setPower((prev) => ({
    //         ...prev,
    //         actualPower: Math.round(_actualPower * 10) / 10,
    //     }));
    // }, [input.beltSpeed, resistance.overall]);

    // useEffect(() => {
    //     if (!power.actualPower || !power.mechanicalEfficiency || Number(power.mechanicalEfficiency) === 0) return;

    //     const _requiredPower = power.actualPower / power.mechanicalEfficiency;
    //     const _selectedMotor = selectMotor(_requiredPower);

    //     setPower((prev) => ({
    //         ...prev,
    //         requiredPower: Math.round(_requiredPower * 10) / 10,
    //         selectedMotor: _selectedMotor,
    //     }));
    // }, [power.actualPower, power.mechanicalEfficiency]);

    /* Tension Analysis */
    // useEffect(() => {
    //     if (
    //         !resistance.overall ||
    //         !tension.wrapAngle ||
    //         Number(tension.wrapAngle) === 0 ||
    //         !tension.pulleyFrictionCoeff ||
    //         Number(tension.pulleyFrictionCoeff) === 0
    //     ) {
    //         return;
    //     }

    //     const FU = resistance.overall;
    //     const theta = (parseFloat(tension.wrapAngle) * Math.PI) / 180; // 라디안
    //     const mu = parseFloat(tension.pulleyFrictionCoeff);
    //     const _drivingFactor = 1 / (Math.pow(Math.E, mu * theta) - 1);
    //     const _slackSide = FU * _drivingFactor;

    //     setTension((prev) => ({
    //         ...prev,
    //         slackSide: Math.ceil(_slackSide),
    //         drivingFactor: Math.round(_drivingFactor * 100) / 100,
    //     }));
    // }, [resistance.overall, tension.pulleyFrictionCoeff, tension.wrapAngle]);

    /* Minimum Tension 계산 */
    // useEffect(() => {
    //     if (!tension.permissibleSlackRate || Number(tension.permissibleSlackRate) === 0) return;

    //     const FU = resistance.overall;
    //     const a0 = parseFloat(input.carrySpacing);
    //     const au = parseFloat(input.returnSpacing);
    //     const g = 9.806;
    //     const qB = output.massBelt;
    //     const qG = output.massMaterial;
    //     const qRU = output.massReturnIdler;
    //     const B = parseFloat(input.beltWidth) / 1000; // m로 환산

    //     const rate = parseFloat(tension.permissibleSlackRate);
    //     const L = parseFloat(input.length);
    //     const delta = (output.slopeAngle * Math.PI) / 180; // 라디안 변환
    //     const f = parseFloat(input.artificialFrictionCoeff);

    //     // Required Min. Tension 계산
    //     const _carrySide = (a0 * (qB + qG) * g) / (8 * rate);
    //     const _returnSide = (au * qB * g) / (8 * rate);
    //     const _minSlackTension = Math.max(_carrySide, _returnSide);
    //     const _slopeTensionReturn = L * (qB * Math.sin(delta) - f * (qRU + qB * Math.cos(delta))) * g;

    //     // Max. Tension 계산
    //     const _max1 = FU + tension.slackSide;
    //     const _max2 = FU + Math.max(_carrySide, _returnSide);
    //     const _max3 = FU + Math.max(_carrySide, _returnSide) + _slopeTensionReturn;

    //     const _maxTension = Math.max(_max1, _max2, _max3);
    //     const _SF = parseFloat(belt.safetyFactor);
    //     const _unitTensionInBelt = (_maxTension * _SF) / B / 1000;

    //     setTension((prev) => ({
    //         ...prev,
    //         permissibleSlackSideTensionCarry: Math.ceil(_carrySide),
    //         permissibleSlackSideTensionReturn: Math.ceil(_returnSide),
    //         minSlackTension: Math.ceil(_minSlackTension),
    //         slopeTensionReturn: Math.ceil(_slopeTensionReturn),
    //         maxTension1: Math.ceil(_max1),
    //         maxTension2: Math.ceil(_max2),
    //         maxTension3: Math.ceil(_max3),
    //         maxTension: Math.ceil(_maxTension),
    //     }));

    //     setBelt((prev) => ({
    //         ...prev,
    //         unitTensionInBelt: Math.ceil(_unitTensionInBelt),
    //     }));
    // }, [
    //     belt.safetyFactor,
    //     input.artificialFrictionCoeff,
    //     input.beltWidth,
    //     input.carrySpacing,
    //     input.length,
    //     input.returnSpacing,
    //     output.massBelt,
    //     output.massMaterial,
    //     output.massReturnIdler,
    //     output.slopeAngle,
    //     resistance.overall,
    //     tension.permissibleSlackRate,
    //     tension.slackSide,
    // ]);

    /* 아이들러 수량, 중량 정보 */
    useEffect(() => {
        if (
            !input.beltWidth ||
            !input.carrySpacing ||
            !input.returnSpacing ||
            !input.length ||
            !input.height ||
            !input.skirtLength
        )
            return;

        const idlers = getIdlerInfo(
            input.beltWidth,
            input.carrySpacing,
            input.returnSpacing,
            input.length,
            input.height,
            input.skirtLength,
        );

        // 리덕스에 쓰기
        dispatch(
            changeBody({
                key: 'idlers',
                value: idlers,
            }),
        );
    }, [
        dispatch,
        input.beltWidth,
        input.carrySpacing,
        input.height,
        input.length,
        input.returnSpacing,
        input.skirtLength,
    ]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        dispatch(
            changeBody({
                key: 'input',
                value: input,
            }),
        );
    }, [dispatch, input]);

    useEffect(() => {
        dispatch(
            changeBody({
                key: 'output',
                value: output,
            }),
        );
    }, [dispatch, output]);

    return (
        <Wrapper>
            <InputSection>
                <Title>
                    <h3 style={{ marginBottom: '-0.5rem' }}>{isEN ? 'Input Section' : isJP ? '入力部' : '입력부'}</h3>
                </Title>
                <Content>
                    <CopyFromOhtersButton setInput={setInput} />
                    <Input
                        label={isEN ? 'Capacity' : isJP ? '運搬量（重量）' : '운반량(중량)'}
                        variable="Q"
                        unit="tph"
                        variableName="capacity"
                        value={input.capacity}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Horizontal Length' : isJP ? '水平機長' : '수평 길이'}
                        variable="L"
                        unit="m"
                        variableName="length"
                        value={input.length}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Elevation Height' : isJP ? '揚程' : '수직 높이'}
                        variable="H"
                        unit="m"
                        variableName="height"
                        value={input.height}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Material Conveyed' : isJP ? '運搬物名称' : '운반물'}
                        variable="-"
                        unit="-"
                        variableName="materialConveyed"
                        value={input.materialConveyed}
                        onChange={handleChange}
                        isText
                    />
                    <Input
                        label={
                            <AlertLabel
                                label={isEN ? 'Apparent Specific Gravity of Material' : isJP ? '嵩比重' : '겉보기 비중'}
                                message={
                                    isEN
                                        ? 'Apparent Specific Gravity(or Bulk Density) of Material'
                                        : isJP
                                        ? '個々の粒子の固有比重ではなく、バルク状態の見かけ比重(嵩比重)'
                                        : '개별 입자의 고유 비중이 아닌 벌크 상태의 겉보기 비중'
                                }
                            />
                        }
                        variable="⍴"
                        unit="kg/m3"
                        variableName="specificGravity"
                        value={input.specificGravity}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Belt Speed' : isJP ? 'ベルト速度' : '벨트 속도'}
                        variable="v"
                        unit="m/s"
                        variableName="beltSpeed"
                        value={input.beltSpeed}
                        onChange={handleChange}
                    />
                    <Select
                        label={isEN ? 'Belt Width' : isJP ? 'ベルト幅' : '벨트 폭'}
                        variable="B"
                        unit="mm"
                        variableName="beltWidth"
                        value={input.beltWidth}
                        options={beltWidthRating}
                        onChange={handleChange}
                    />
                    <Select
                        label={isEN ? 'Belt Carcass Type' : isJP ? 'ベルト芯体種別' : '벨트 심체의 종류'}
                        variable="-"
                        unit="-"
                        variableName="carcassType"
                        value={input.carcassType}
                        options={beltCarcassType}
                        onChange={handleChange}
                    />
                    <Input
                        label={
                            isEN
                                ? 'Artificial Friction Coefficient'
                                : isJP
                                ? 'ローラー回転摩擦係数'
                                : '롤러의 회전부 마찰계수'
                        }
                        variable="f"
                        unit="-"
                        variableName="artificialFrictionCoeff"
                        value={input.artificialFrictionCoeff}
                        onChange={handleChange}
                        referenceValue={0.02}
                    />
                    <Input
                        label="Number of Pulleys"
                        variable="-"
                        unit="sets"
                        variableName="qtyPulley"
                        value={input.qtyPulley}
                        onChange={handleChange}
                        referenceValue={6}
                    />
                    <Input
                        label={
                            isEN
                                ? 'Installation Spacing of Carrying Idlers'
                                : isJP
                                ? 'パネルーピッチ(キャリヤ)'
                                : '아이들러 피치(캐리어)'
                        }
                        variable="a0"
                        unit="m"
                        variableName="carrySpacing"
                        value={input.carrySpacing}
                        onChange={handleChange}
                        referenceValue={1.0}
                    />
                    <Input
                        label={
                            isEN
                                ? 'Installation Spacing of Return Idlers'
                                : isJP
                                ? 'パネルーピッチ(レタン)'
                                : '아이들러 피치(리턴)'
                        }
                        variable="au"
                        unit="m"
                        variableName="returnSpacing"
                        value={input.returnSpacing}
                        onChange={handleChange}
                        referenceValue={3.0}
                    />
                    <Input
                        label="Trough Angle"
                        variable="𝜆" // lamda
                        unit="deg."
                        variableName="troughAngle"
                        value={input.troughAngle}
                        onChange={handleChange}
                        referenceValue={30}
                    />
                    <Input
                        label={
                            <AlertLabel
                            label={isEN ? "Initial Speed of Material Handled" : isJP ? "運搬物のベルト進行方向速度" : "운반물의 벨트진행 방향 속도"}
                                message={
                                    isJP
                                        ? '積載点での 運搬物の ベルト進行方向に対する速度。\n運搬物がベルトに垂直に落下する場合、速度は「0」となる。'
                                        : isEN
                                        ? "The speed of the material in the belt's direction of travel at the Loading Point.\nIf the material falls perpendicularly onto the belt, the speed is '0'."
                                        : "Loading Point에서 운반물의 벨트 진행방향에 대한 속도.\n운반물이 벨트에 수직으로 떨어지는 경우에는 '0'이다."
                                }
                            />
                        }
                        variable="v0"
                        unit="m/s"
                        variableName="initialSpeed"
                        value={input.initialSpeed}
                        onChange={handleChange}
                        referenceValue={0.0}
                    />
                    <Input
                        label="Front Tile Angle (Angle at Belt Plane)"
                        variable="𝜀" // epsilon
                        unit="deg."
                        variableName="tiltAngle"
                        value={input.tiltAngle}
                        onChange={handleChange}
                        referenceValue={0.0}
                    />
                    <Input
                        label="Conveyor Length of Front Tilt Type Idlers Part"
                        variable="L𝜀" // Lepsilon
                        unit="m"
                        variableName="tiltLength"
                        value={input.tiltLength}
                        onChange={handleChange}
                        referenceValue={0.0}
                    />
                    <Input
                        label="Friction Coefficient between the Idlers and the Belt"
                        variable="𝜇0"
                        unit="-"
                        variableName="frictionCoeffIdlerBelt"
                        value={input.frictionCoeffIdlerBelt}
                        onChange={handleChange}
                        referenceValue={0.4}
                    />
                    <Input
                        label="Friction Coefficient between the Mateiral and the Belt"
                        variable="𝜇1"
                        unit="-"
                        variableName="frictionCoeffMaterialBelt"
                        value={input.frictionCoeffMaterialBelt}
                        onChange={handleChange}
                        referenceValue={0.7}
                    />
                    <Input
                        label="Friction Coefficient between the Belt and the Skirtplate"
                        variable="𝜇2"
                        unit="-"
                        variableName="frictionCoeffBeltSkirt"
                        value={input.frictionCoeffBeltSkirt}
                        onChange={handleChange}
                        referenceValue={0.7}
                    />
                    <Input
                        label="Friction Coefficient between the Belt and the Cleaner"
                        variable="𝜇3"
                        unit="-"
                        variableName="frictionCoeffBeltCleaner"
                        value={input.frictionCoeffBeltCleaner}
                        onChange={handleChange}
                        referenceValue={0.5}
                    />
                    <Input
                        label="Q'ty of (External) Belt Cleaner"
                        variable="-"
                        unit="set(s)"
                        variableName="qtyBeltCleaner"
                        value={input.qtyBeltCleaner}
                        onChange={handleChange}
                        referenceValue={2}
                    />
                    <Input
                        label="Pressure between the Belt and the Cleaner"
                        variable="P"
                        unit="N/m2"
                        variableName="cleanerContactPressure"
                        value={input.cleanerContactPressure}
                        onChange={handleChange}
                        referenceValue={65000}
                    />
                    <Input
                        label="Q'ty of Unloading Scraper"
                        variable="-"
                        unit="set(s)"
                        variableName="qtyUnloadingScraper"
                        value={input.qtyUnloadingScraper}
                        onChange={handleChange}
                    />
                    <Input
                        label="Scraping Coefficient of Unloading Scraper"
                        variable="ka"
                        unit="N/m"
                        variableName="scrapingCoeff"
                        value={input.scrapingCoeff}
                        onChange={handleChange}
                        referenceValue={1500}
                    />
                </Content>
            </InputSection>
            <InterCalcSection>
                <Title>
                    <h3>{isEN ? 'Internal Calculation' : isJP ? '内部計算結果' : '내부계산 결과'}</h3>
                </Title>
                <Content>
                    <Output
                        label={isEN ? 'Length Correction Factor' : isJP ? '中心距離修正値' : '중심거리 보정계수'}
                        variable="C"
                        unit="-"
                        variableName="lengthCoeff"
                        value={output.lengthCoeff}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Mass of Rotating Parts of Idler along the Carrying Side'
                                : isJP
                                ? 'パイプ部ローラー回転部重量(キャリヤ)'
                                : '롤러회전부 중량(캐리어측)'
                        }
                        variable="qRO"
                        unit="kg/m"
                        variableName="massCarryIdler"
                        value={output.massCarryIdler}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Mass of Rotating Parts of Idler along the Return Side'
                                : isJP
                                ? 'パイプ部ローラー回転部重量(リタン)'
                                : '롤러회전부 중량(리턴측)'
                        }
                        variable="qRU"
                        unit="kg/m"
                        variableName="massReturnIdler"
                        value={output.massReturnIdler}
                    />
                    <Output
                        label={isEN ? 'Mass of Belt by JIS' : isJP ? 'ベルト重量(JIS基準)' : '벨트 중량(JIS기준)'}
                        variable="qB"
                        unit="kg/m"
                        variableName="massBelt"
                        value={output.massBelt}
                    />
                    <Output
                        label="Belt Thickness by JIS"
                        variable="d"
                        unit="mm"
                        variableName="beltThick"
                        value={output.beltThick}
                    />
                    <Output
                        label={isEN ? 'Volumetric Capacity' : isJP ? '運搬量（容積）' : '운반량(용적)'}
                        variable="Iv"
                        unit="m3/s"
                        variableName="volumetricCapacity"
                        value={output.volumetricCapacity}
                    />
                    <Output
                        label="Skirtplate Width"
                        variable="b1"
                        unit="m"
                        variableName="skirtWidth"
                        value={output.skirtWidth}
                    />
                    <Output
                        label="Acceleration Area Length(@Skirt)"
                        variable="Ib"
                        unit="m"
                        variableName="accelerationLength"
                        value={output.accelerationLength}
                    />
                    <Input
                        label={isEN ? 'Skirtplate Length' : isJP ? 'スカートの長さ' : '스커트 길이'}
                        variable="l"
                        unit="m"
                        variableName="skirtLength"
                        value={input.skirtLength}
                        onChange={handleChange}
                    />
                    <Output
                        label={isEN ? 'Mass of Material Handled' : isJP ? '運搬物の重量' : '운반물 중량'}
                        variable="qG"
                        unit="kg/m"
                        variableName="massMaterial"
                        value={output.massMaterial}
                    />
                    <Output
                        label="Slope Angle of Conveyor"
                        variable="𝛿"
                        unit="deg."
                        variableName="slopeAngle"
                        value={output.slopeAngle}
                    />
                    <Output
                        label="Trough Angle Coefficient"
                        variable="C𝜀" // Cepsilon
                        unit="deg."
                        variableName="troughAngleCoeff"
                        value={output.troughAngleCoeff}
                    />
                    <Output
                        label="Contact Area of Cleaner with Belt"
                        variable="A"
                        unit="m2"
                        variableName="cleanerContactArea"
                        value={output.cleanerContactArea}
                    />
                </Content>
            </InterCalcSection>
            <ResistanceSection>
                <Title>
                    <h3>Resistance</h3>
                </Title>
                <Content>
                    <LoadNoLoadLabel />
                    <MultiOutput
                        label="Main Resistance"
                        variable="FH"
                        unit="N"
                        variableName="main"
                        values={[resistance?.main?.load, resistance?.main?.noLoad]}
                        // values={[100, 200]}
                    />
                    <MultiOutput
                        label="Secondary Resistance"
                        variable="FN"
                        variableName="secondary"
                        unit="N"
                        values={[resistance?.secondary?.load, resistance?.secondary?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Acceleration Resistance of Material in Acc. Area"
                        variable="FN"
                        variableName="secondary1"
                        unit="N"
                        values={[resistance?.secondary1?.load, resistance?.secondary1?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance between Material and the Skirt Plates in Acc. Area"
                        variable="Ff"
                        variableName="secondary2"
                        unit="N"
                        values={[resistance?.secondary2?.load, resistance?.secondary2?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Wrapping Resistance of Belt on Pulleys"
                        variable="Ft"
                        variableName="secondary3"
                        unit="N"
                        values={[resistance?.secondary3?.load, resistance?.secondary3?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Bearing Resistance of Pulley (Except Driving Pulley)"
                        variable="Ft"
                        variableName="secondary4"
                        unit="N"
                        values={[resistance?.secondary4?.load, resistance?.secondary4?.noLoad]}
                    />
                    <MultiOutput
                        label="Special Main Resistance"
                        variable="FN"
                        variableName="specialMain"
                        unit="N"
                        values={[resistance?.specialMain?.load, resistance?.specialMain?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance when Idlers have tilted frontward"
                        variable="F𝜀" // Fepsilon
                        variableName="specialMain1"
                        unit="N"
                        values={[resistance?.specialMain1?.load, resistance?.specialMain1?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance between Material and Skirtplates outside Acc. Area "
                        variable="FgL"
                        variableName="specialMain2"
                        unit="N"
                        values={[resistance?.specialMain2?.load, resistance?.specialMain2?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Friction Resistance due to Belt Cleaner"
                        variable="Fr"
                        variableName="specialMain3"
                        unit="N"
                        values={[resistance?.specialMain3?.load, resistance?.specialMain3?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Friction Resistance of Unloading Scraper"
                        variable="Fa"
                        variableName="specialMain4"
                        unit="N"
                        values={[resistance?.specialMain4?.load, resistance?.specialMain4?.noLoad]}
                    />
                    <MultiOutput
                        label="Special Secondary Resistance, if Any"
                        variable="Fs2"
                        variableName="specialSecondary"
                        unit="N"
                        values={[resistance?.specialSecondary?.load, resistance?.specialSecondary?.noLoad]}
                    />
                    <MultiOutput
                        label="Slope Resistance"
                        variable="FST"
                        variableName="slope"
                        unit="N"
                        values={[resistance?.slope?.load, resistance?.slope?.noLoad]}
                    />
                    {/* <MultiOutput
                        label={
                            <AlertLabel
                                label="Horizontal Curve Resistance"
                                message="아래 Tension 계산 후 계산하면 자동으로 입력됩니다.\n以下のテンション計算後に計算すると自動的に入力されます。"
                            />
                        }
                        variable="-"
                        unit="N"
                        variableName="horizontalCurveResistance"
                        values={[horizontalCurveResistance?.load, horizontalCurveResistance?.noLoad]}
                        // values={[5000, 5000]}
                    /> */}
                    <MultiOutput
                        label="Overall Resistance"
                        variable="FU"
                        variableName="overall"
                        unit="N"
                        values={[resistance?.overall?.load, resistance?.overall?.noLoad]}
                    />
                </Content>
            </ResistanceSection>
        </Wrapper>
    );
};

export default PowerCalculationJIS;

/* =================   Styles   ================== */
const Wrapper = styled.div``;
const InputSection = styled.div``;
const InterCalcSection = styled.div``;
const ResistanceSection = styled.div``;
const Title = styled.div``;
const Content = styled.div``;
